import {AlertCircle, AlertTriangle, Info} from 'lucide-react';

type Severity = 'info' | 'warning' | 'error';
type Variant = 'light' | 'dark';

interface AlertProps {
  severity?: Severity;
  variant?: Variant;
  className?: string;
  children: React.ReactNode;
}

// Per severity: the icon, its dark-variant color (in the light variant the icon
// inherits the wrapper's text color), and the solid left-border accent shared by
// both variants.
const SEVERITY: Record<Severity, {Icon: typeof Info; iconDark: string; accent: string}> = {
  info: {Icon: Info, iconDark: 'text-sky-400', accent: 'border-l-sky-400'},
  warning: {Icon: AlertTriangle, iconDark: 'text-iq-orange', accent: 'border-l-iq-orange'},
  error: {Icon: AlertCircle, iconDark: 'text-red-400', accent: 'border-l-red-500'},
};

const WRAPPER: Record<Variant, Record<Severity, string>> = {
  light: {
    info: 'bg-sky-50 border-sky-200 text-sky-800',
    warning: 'bg-iq-orange-light border-orange-200 text-iq-orange-dark',
    error: 'bg-red-50 border-red-200 text-iq-red',
  },
  dark: {
    info: 'bg-sky-400/15 border-sky-400/60 text-white',
    warning: 'bg-iq-orange/15 border-iq-orange/60 text-white',
    error: 'bg-red-500/15 border-red-500/60 text-white',
  },
};

export function Alert({
  severity = 'info',
  variant = 'light',
  className = '',
  children,
}: AlertProps) {
  const {Icon, iconDark, accent} = SEVERITY[severity];
  return (
    <div
      role='alert'
      className={`flex items-start gap-3 rounded-lg border border-l-4 px-4 py-3 text-sm ${WRAPPER[variant][severity]} ${accent} ${className}`}>
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${variant === 'dark' ? iconDark : ''}`} />
      <span>{children}</span>
    </div>
  );
}
